import { PlaylistDownloadDto, PlaylistItemStateDownloadDto, PlaylistStateDownloadDto } from "collaboration-service";
import SafeHTML from "components/SafeHTML/SafeHTML";
import { Icon } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { addHoursToISODate, convertMinutesToDaysHoursMinutes, shallowCompare, sortReferenceIdsInNodes } from "services/Helpers";
import { getTranslated, getTranslatedStandardDate } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { useAppTheme, useBelowBreakpointOrEqual } from "services/useAppTheme";
import { RefPostsInterface } from "views/Playlist/PlaylistDetail";
import PlaylistDetailNodeItem from "views/Playlist/PlaylistDetailNodeItem";
import { PlaylistDetailSC as T } from 'views/Playlist/PlaylistDetailSC';
import { PlaylistTheme } from "views/Playlist/PlaylistMain";


interface PlaylistDetailNodesToShowProps {
    playlistState?: PlaylistStateDownloadDto;
    refPosts: RefPostsInterface;
    reloadPlaylistState: (toBeDone?: string) => Promise<void>;
    palmsLinks: Record<string, string>;
}

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage
})

const PlaylistDetailNextNodes = (p: PlaylistDetailNodesToShowProps) => {
    const { contentLanguage } = useAppSelector(mapper, shallowCompare);
    const { playlistState, refPosts, reloadPlaylistState, palmsLinks } = p;
    const { t } = useImgI18N("playlist");
    const playlistTheme = useAppTheme<PlaylistTheme>("playlistTheme");
    const flushText = playlistTheme.flushText;

    const [visibleNodes, setVisibleNodes] = React.useState<{ [key: string]: boolean }>({});
    const isMobile = useBelowBreakpointOrEqual("largeMobile");

    const toggleVisibility = React.useCallback((nodeId: string) => {
        setVisibleNodes((prev) => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }));
    }, []);

    const {
        nextNodes,
        playlistStateIsOptional
    } = React.useMemo(() => {
        const nextNodes = sortReferenceIdsInNodes(playlistState?.nextNodes ?? []);
        const playlistStateIsOptional = playlistState?.optional;
        return {
            nextNodes,
            playlistStateIsOptional
        }
    }, [playlistState])

    return (
        <React.Fragment>
            {_.map(nextNodes, (n) => {
                const isAbsDueDate = n.dueDateRelativeTo && n.dueDateRelativeTo === "Absolute";
                const isRelDueDate = n.dueDateRelativeTo && n.dueDateRelativeTo !== "Absolute";
                const hasAbsDueDate = isAbsDueDate && n.absDueDate;
                const hasRelDueDate = isRelDueDate && n.dueDateRelativeInHours && n.dueDateRelativeInHours > 0;
                const dueToDate = hasAbsDueDate ? n.absDueDate : (hasRelDueDate && playlistState?.assignmentDate && n.dueDateRelativeInHours) ? addHoursToISODate(playlistState.assignmentDate, n.dueDateRelativeInHours) : undefined;
                const dueToDateText = dueToDate && getTranslatedStandardDate(dueToDate, "PP");
                const estDuration = n.durationInMinutes ? convertMinutesToDaysHoursMinutes(n.durationInMinutes, t, true) : undefined;
                const isVisible = visibleNodes[n.id as string] ?? false;
                const showPreviewButton = n.visible === true;

                return (
                    <React.Fragment key={n.id}>
                        <T.NodeBarNext flushText={flushText} active={showPreviewButton} onClick={showPreviewButton ? () => toggleVisibility(n.id as string) : undefined}>
                            <T.NodeBarTitle style={{ display: "grid", gridTemplateColumns: "max-content 1fr max-content" }}>
                                {showPreviewButton ?
                                    <T.NodeBarIconPreview isVisible={isVisible}>
                                        <Icon name={isVisible ? 'chevron up' : 'chevron down'} size="20px" style={{ float: "left" }} marginTop={0} />
                                    </T.NodeBarIconPreview>
                                    :
                                    <div />
                                }
                                <div style={{ paddingLeft: showPreviewButton ? 0 : 4 }}>
                                    <SafeHTML html={getTranslated(n.headlines, contentLanguage)?.text} allowedTags={[]} allowedAttributes={{}} />
                                </div>
                                {showPreviewButton && isVisible ?
                                    <T.NodeBarTextPreview>
                                        <Icon name="info" style={{ float: "left", marginRight: 5 }} marginTop={1} color='@accentRed' />
                                        {t("preview only")}
                                    </T.NodeBarTextPreview>
                                    :
                                    <div />
                                }
                            </T.NodeBarTitle>
                            <T.NodeBarInfo flushText={!flushText} style={{ width: 100 }}>{estDuration}</T.NodeBarInfo>
                            <div>
                                {!playlistStateIsOptional &&
                                    <T.NodeBarInfo flushText={flushText} style={{ width: 150 }}>
                                        {_.first(n.children)?.transitionFunction === "FallThru" && <T.DueDateOptionalHint>({t("optional")})</T.DueDateOptionalHint>}
                                        {dueToDateText}
                                    </T.NodeBarInfo>
                                }
                            </div>

                        </T.NodeBarNext>
                        {isVisible &&
                            <T.NodeBarPreviewContainer>
                                {_.map(n.referenceIds, (refId, idx) => {
                                    const p = refPosts[refId];
                                    if (p === undefined)
                                        return false;
                                    const key = p.id;
                                    const isOptional = false;
                                    const state = _.find(playlistState?.itemStates, st => st.referenceId === refId)
                                        ?? _.find(playlistState?.prevItemStates, st => st.referenceId === refId);

                                    const firstChild = _.first(n.children);
                                    const transIsRef = firstChild?.transitionFunction === "ReferenceDone" || firstChild?.transitionFunction === "ReferenceDoneWithValue";
                                    const transIsRefVal = (firstChild && firstChild.referenceValue);
                                    const nodeReferences: string[] | undefined = (firstChild && transIsRef && transIsRefVal) ? firstChild.referenceContentIds : undefined;
                                    const nodeReference = _.find(nodeReferences, nr => nr === state?.referenceId) ?? undefined;
                                    const content = _.find((playlistState?.playlist as PlaylistDownloadDto)?.contents, c => c.referenceId === refId);
                                    return (
                                        <React.Fragment key={key}>
                                            <PlaylistDetailNodeItem
                                                item={p}
                                                isMobile={isMobile}
                                                state={state ?? { state: 'NotAvailable' } as PlaylistItemStateDownloadDto}
                                                playlistId={playlistState?.playlist?.id}
                                                orderNumber={idx + 1}
                                                playlistStateId={playlistState?.id ?? ''}
                                                isOptional={isOptional || playlistStateIsOptional}
                                                dueToType={undefined}
                                                assignmentDate={playlistState?.assignmentDate}
                                                absoluteDueDate={n.absDueDate}
                                                dueDateRelativeInHours={n.dueDateRelativeInHours}
                                                nodeReference={nodeReference}
                                                transition={firstChild?.transitionFunction}
                                                accentColor=''
                                                loadPlaylistStates={reloadPlaylistState}
                                                playlistContent={content}
                                                preview={true}
                                                palmsLinks={palmsLinks}
                                                node={n}

                                            />
                                        </React.Fragment>
                                    );
                                })}
                            </T.NodeBarPreviewContainer>
                        }
                    </React.Fragment>
                );
            })}
        </React.Fragment>
    )
}

export default PlaylistDetailNextNodes;